<template>
    <div>
        <vx-navbar color="light" :responsiveOffset="20" class="app-navbar mb-6 md:mb-0">
            <template v-slot:left>
                <vx-navbar-action
                    href="/kiosk/teams"
                    class="hide-action-icon"
                    :marker="false"
                    data-test="nav-home"
                    aria-label="Kiosk Mode"
                >
                    <navbar-logo></navbar-logo>
                    <span class="hidden text-base text-slate-800 sm:block">Kiosk Mode</span>
                </vx-navbar-action>
            </template>

            <template v-slot:right>
                <vx-button href="/" size="sm" color="muted-dark">
                    Exit Kiosk Mode
                    <font-awesome-icon widthAuto :icon="faPersonToDoor" class="text-sm"></font-awesome-icon>
                </vx-button>
            </template>

            <vx-navbar-action
                v-for="bucket in buckets"
                :key="bucket.key"
                color="light"
                :icon="bucket.icon"
                :active="activeBucket === bucket.key"
            >
                <span>{{ bucket.name }}</span>

                <template v-slot:items>
                    <vx-navbar-action-item
                        v-for="item in bucket.items"
                        :key="item.key"
                        :href="item.href"
                        :active="activeItem === item.key"
                        :data-test="`kiosk-nav-item-${item.key}`"
                    >
                        {{ item.name }}
                    </vx-navbar-action-item>
                </template>
            </vx-navbar-action>
        </vx-navbar>

        <div class="hidden pt-6 px-6 md:block sm:h-12 md:px-8 sm:mb-8">
            <vx-navigation v-if="activeBucketItems.length">
                <vx-navigation-item
                    v-for="item in activeBucketItems"
                    :key="`subnav-${item.key}`"
                    :value="item.key"
                    :href="item.href"
                    :active="activeItem === item.key"
                    :data-test="`kiosk-subnav-${item.key}`"
                >
                    {{ item.name }}
                </vx-navigation-item>
            </vx-navigation>
        </div>
    </div>
    <div data-test="kiosk-layout-container" :class="containerClasses">
        <slot></slot>
    </div>
</template>

<script setup>
import { faPersonToDoor, faScrewdriverWrench, faSitemap, faTowerCell } from '@fortawesome/pro-solid-svg-icons';
import {
    VxButton,
    VxNavbar,
    VxNavbarAction,
    VxNavbarActionItem,
    VxNavigation,
    VxNavigationItem,
} from '@voxie/frontend-components';
import { match } from 'path-to-regexp';
import { computed } from 'vue';
import NavbarLogo from '~/components/navbar/NavbarLogo.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    formContainer: {
        type: Boolean,
        default: false,
    },
});

const containerClasses = computed(() =>
    props.formContainer
        ? 'container mx-auto px-3 pb-64 2xl:px-0 max-w-(--breakpoint-xl)'
        : 'space-y-8 md:space-y-12 pb-32 px-4 sm:px-6 md:px-8'
);

const buckets = [
    {
        key: 'directory',
        name: 'Directory',
        icon: faSitemap,
        items: [
            { key: 'teams', name: 'Teams', href: '/kiosk/teams' },
            { key: 'org-units', name: 'Organizational Units', href: '/kiosk/org-units' },
            { key: 'groups', name: 'Groups', href: '/kiosk/groups' },
            { key: 'users', name: 'Users', href: '/kiosk/users' },
        ],
    },
    {
        key: 'operations',
        name: 'Operations',
        icon: faScrewdriverWrench,
        items: [
            { key: 'cloning', name: 'Cloning', href: '/kiosk/cloning' },
            { key: 'migrations', name: 'Migrations', href: '/kiosk/migrations' },
        ],
    },
    {
        key: 'ten-dlc',
        name: '10DLC',
        icon: faTowerCell,
        items: [
            { key: 'brands', name: 'Brands', href: '/kiosk/ten-dlc/brands' },
            { key: 'campaigns', name: 'Campaigns', href: '/kiosk/ten-dlc/campaigns' },
            { key: 'phone-numbers', name: 'Phone Numbers', href: '/kiosk/ten-dlc/phone-numbers' },
        ],
    },
];

// Order matters: first match wins. Specific nested routes must precede
// their catch-all parent. "imports" must precede ":groupId" because
// path-to-regexp would match "imports" as a :groupId param value.
const navRoutes = [
    { matcher: match('/kiosk/teams/:team/org-units{/*path}'), bucket: 'directory', item: 'org-units' },
    { matcher: match('/kiosk/teams/:team/groups/imports{/*path}'), bucket: 'directory', item: 'teams' },
    { matcher: match('/kiosk/teams/:team/groups/:groupId{/*path}'), bucket: 'directory', item: 'groups' },
    { matcher: match('/kiosk/teams{/*path}'), bucket: 'directory', item: 'teams' },
    { matcher: match('/kiosk/org-units{/*path}'), bucket: 'directory', item: 'org-units' },
    { matcher: match('/kiosk/groups{/*path}'), bucket: 'directory', item: 'groups' },
    { matcher: match('/kiosk/users{/*path}'), bucket: 'directory', item: 'users' },
    { matcher: match('/kiosk/ten-dlc/campaigns{/*path}'), bucket: 'ten-dlc', item: 'campaigns' },
    { matcher: match('/kiosk/ten-dlc/phone-numbers{/*path}'), bucket: 'ten-dlc', item: 'phone-numbers' },
    { matcher: match('/kiosk/ten-dlc{/*path}'), bucket: 'ten-dlc', item: 'brands' },
    { matcher: match('/kiosk/cloning{/*path}'), bucket: 'operations', item: 'cloning' },
    { matcher: match('/kiosk/migrations{/*path}'), bucket: 'operations', item: 'migrations' },
];

const activeRoute = navRoutes.find((route) => route.matcher(window.location.pathname)) ?? null;
const activeBucket = activeRoute?.bucket ?? null;
const activeItem = activeRoute?.item ?? null;
const activeBucketItems = buckets.find((bucket) => bucket.key === activeBucket)?.items ?? [];
</script>
